import { mediaIdImageField } from '@/lib/mediaImageField';
import { AttractionThemeImageCell, ThemeStatusBadge } from '@/components/attractions/themes';
import { ThemeTypeDropdown } from '@/components/dropdowns/ThemeTypeDropdown';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Palette, Plus } from 'lucide-react';
import { z } from 'zod';

/**
 * Zod schemas mirroring server-side attraction-themes.schemas.ts.
 * The create schema includes attractionId (injected by the API route),
 * but we omit it here since CrudFormView doesn't send it as a form field.
 */
const createAttractionThemeSchema = z.object({
  themeTypeId: z.string().uuid('Theme type ID must be a valid UUID'),
  name: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  longDescription: z.string().optional().nullable(),
  featuredImageId: z.string().uuid('Featured image ID must be a valid UUID').optional().nullable(),
  status: z.enum(['draft', 'published', 'needs_review']).optional().default('draft'),
});

const updateAttractionThemeSchema = createAttractionThemeSchema.partial();

export interface AttractionTheme {
  id: string;
  attractionId: string;
  themeTypeId: string;
  name?: string | null;
  description?: string | null;
  longDescription?: string | null;
  featuredImageId?: string | null;
  voiceoverId?: string | null;
  longVoiceoverId?: string | null;
  status: 'draft' | 'published' | 'needs_review';
  createdAt: string;
  updatedAt: string;
  attraction?: {
    id: string;
    name: string;
  };
  themeType?: {
    id: string;
    name: string;
    slug: string;
    tone?: string | null;
  };
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    url?: string;
  };
  voiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  longVoiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  // Sync status fields (computed by API)
  isDescriptionVoiceoverInSync?: boolean | null;
  isLongDescriptionVoiceoverInSync?: boolean | null;
}

/**
 * Factory function to create attraction themes config for a specific attraction
 */
export const createAttractionThemesConfig = (
  attractionId: string,
  options?: {
    onCreateTheme?: () => void;
  },
): CrudConfig<AttractionTheme> => ({
  entityName: 'Attraction Theme',
  entityNamePlural: 'Attraction Themes',

  endpoints: {
    list: `/attractions/${attractionId}/themes`,
    single: (id: string) => `/attractions/${attractionId}/themes/${id}`,
    create: `/attractions/${attractionId}/themes`,
    update: (id: string) => `/attractions/${attractionId}/themes/${id}`,
    delete: (id: string) => `/attractions/${attractionId}/themes/${id}`,
  },

  fields: [
    {
      key: 'themeTypeId',
      label: 'Theme Type',
      type: 'custom',
      required: true,
      placeholder: 'Select Theme Type',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => <ThemeTypeDropdown value={value} onChange={onChange} placeholder={field.placeholder} />,
    },
    {
      key: 'name',
      label: 'Name Override',
      type: 'text',
      required: false,
      placeholder: 'Optional name override for this theme',
      helpText: 'Override the attraction name for this specific theme (leave blank to use default)',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Short Description',
      type: 'richtext',
      required: false,
      placeholder: 'Short themed description (~200 words)',
      helpText: 'Brief description for this theme variant',
    },
    {
      key: 'longDescription',
      label: 'Long Description',
      type: 'richtext',
      required: false,
      placeholder: 'Long themed description (~600 words)',
      helpText: 'Detailed description for this theme variant',
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      placeholder: 'Upload themed featured image',
      helpText: 'Theme-specific featured image (overrides default attraction image)',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AttractionTheme['featuredImage'], fullRecord?: AttractionTheme) => {
        return <AttractionThemeImageCell attractionTheme={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: AttractionTheme['featuredImage'], fullRecord?: AttractionTheme) => {
        return <AttractionThemeImageCell attractionTheme={fullRecord} size="lg" />;
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'draft',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      helpText: 'Publication status of this themed content',
      format: (value: AttractionTheme['status']) => {
        return <ThemeStatusBadge status={value} />;
      },
    },
    {
      key: 'voiceoverId',
      label: 'Short Voiceover',
      type: 'text',
      required: false,
      readonly: true,
      helpText: 'AI-generated voiceover for short description',
    },
    {
      key: 'voiceover',
      label: 'Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: AttractionTheme['voiceover'], fullRecord?: AttractionTheme) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'longVoiceoverId',
      label: 'Long Voiceover',
      type: 'text',
      required: false,
      readonly: true,
      helpText: 'AI-generated voiceover for long description',
    },
    {
      key: 'longVoiceover',
      label: 'Long Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: AttractionTheme['longVoiceover'], fullRecord?: AttractionTheme) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isLongDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Long description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'themeType',
      label: 'Theme Type',
      type: 'custom',
      required: false,
      format: (_value: AttractionTheme['themeType'], fullRecord?: AttractionTheme) => {
        return fullRecord?.themeType?.name || '—';
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['themeType', 'status', 'name', 'createdAt'],
    searchableFields: ['name', 'description'],
    sortableFields: ['createdAt', 'status'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    emptyState: {
      icon: <Palette className="h-12 w-12" />,
      title: 'No themes yet',
      description: 'Create a base theme to enable multi-language translations for this attraction',
      action: options?.onCreateTheme
        ? {
            label: 'Create Theme',
            icon: Plus,
            onClick: options.onCreateTheme,
          }
        : undefined,
    },
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Media',
        description: 'Theme-specific images',
        fields: ['featuredImageLarge'],
        defaultExpanded: true,
      },
      {
        title: 'Theme Information',
        description: 'Theme type and configuration',
        fields: ['themeType', 'status', 'name'],
        defaultExpanded: true,
      },
      {
        title: 'Descriptions',
        description: 'Short and long themed descriptions',
        fields: ['voiceover', 'description', 'longVoiceover', 'longDescription'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    createSchema: createAttractionThemeSchema,
    updateSchema: updateAttractionThemeSchema,
    sections: [
      {
        title: 'Media',
        description: 'Theme-specific images',
        fields: ['featuredImageId'],
        columns: 1,
      },
      {
        title: 'Theme Configuration',
        description: 'Select theme type and status',
        fields: ['themeTypeId', 'status'],
        columns: 1,
      },
      {
        title: 'Content',
        description: 'Themed content and descriptions',
        fields: ['name', 'description', 'longDescription'],
        columns: 1,
      },
    ],
  },
});
